/**
 * Преобразуване на местно софийско време към UTC (Раздел 4.1 от ТЗ).
 *
 * Централата записва времето в името на файла като местно време без отместване.
 * Два дни в годината това е двусмислено или невъзможно, затова преобразуването
 * се прави явно, а не чрез `new Date(...)` на средата, чиято зона е неизвестна.
 */
export const CALL_TIME_ZONE = 'Europe/Sofia';
const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: CALL_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});
/** Стенният часовник в София за даден момент, изразен като UTC милисекунди. */
function wallClockMs(instant) {
    const parts = formatter.formatToParts(instant);
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        return part ? Number(part.value) : Number.NaN;
    };
    // Някои среди връщат час 24 вместо 0 за полунощ.
    const hour = read('hour') % 24;
    return Date.UTC(read('year'), read('month') - 1, read('day'), hour, read('minute'), read('second'));
}
/** Проверява дали компонентите описват съществуваща дата и час в календара. */
export function isValidWallClock({ year, month, day, hour, minute, second }) {
    if (month < 1 || month > 12)
        return false;
    if (day < 1 || day > 31)
        return false;
    if (hour > 23 || minute > 59 || second > 59)
        return false;
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}
/**
 * Превръща местно софийско време в UTC момент.
 *
 * Връща `null`, когато часът не съществува заради преминаване към лятно време.
 * При двусмислен час (връщане на времето назад) се избира първото срещане.
 */
export function sofiaWallClockToUtc(wall) {
    if (!isValidWallClock(wall))
        return null;
    const target = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    const DAY_MS = 86_400_000;
    // Отместването зависи от самия момент, а в деня на смяна на времето една стенна
    // стойност може да съответства на два момента или на нито един. Затова се пробват
    // отместванията от предишния ден, от самия момент и от следващия ден, и се проверява
    // кои от получените кандидати наистина се показват като търсеното местно време.
    const candidates = [...new Set([target - DAY_MS, target, target + DAY_MS])]
        .map((probe) => target - (wallClockMs(new Date(probe)) - probe))
        .filter((candidate) => wallClockMs(new Date(candidate)) === target)
        .sort((left, right) => left - right);
    const earliest = candidates[0];
    return earliest === undefined ? null : new Date(earliest);
}
const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** Разпознава „ЧЧ:ММ“. Връща `null` за всичко останало. */
export function parseTimeOfDay(value) {
    const match = TIME_OF_DAY_PATTERN.exec(value);
    if (!match)
        return null;
    return { hour: Number(match[1]), minute: Number(match[2]) };
}
const pad = (value) => String(value).padStart(2, '0');
/** Долната граница на диапазона: началото на избраната минута. */
export function timeOfDayStart({ hour, minute }) {
    return `${pad(hour)}:${pad(minute)}:00.000000`;
}
/**
 * Горната граница на диапазона: краят на избраната минута.
 *
 * Изборът е с точност до минута, затова „до 12:05“ включва и разговор,
 * започнал в 12:05:30.
 */
export function timeOfDayEnd({ hour, minute }) {
    return `${pad(hour)}:${pad(minute)}:59.999999`;
}
/**
 * Проверява, че диапазонът не минава през полунощ.
 *
 * Обърнат диапазон (22:00–02:00) би върнал разговори от следващия ден и би
 * противоречил на избраната дата, затова се отказва вместо да се тълкува.
 */
export function isTimeOfDayRangeOrdered(from, to) {
    return from.hour * 60 + from.minute <= to.hour * 60 + to.minute;
}
//# sourceMappingURL=time.js.map